// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

export { AzureKeyCredential } from "@azure/core-auth";
export {
  AutocompleteItem,
  AutocompleteMode,
  AutocompleteResult,
  FacetResult,
  IndexActionType,
  IndexDocumentsResult,
  IndexingResult,
  KnownSemanticErrorMode,
  KnownSemanticErrorReason,
  KnownSemanticSearchResultsType,
  KnownVectorFilterMode,
  KnownVectorQueryKind,
  QueryAnswerResult,
  QueryCaptionResult,
  QueryType,
  ScoringStatistics,
  SearchMode,
} from "./generated/data/models";
export {
  AnalyzedTokenInfo,
  AnalyzeResult,
  AsciiFoldingTokenFilter,
  AzureActiveDirectoryApplicationCredentials,
  AzureOpenAIModelName,
  BinaryQuantizationCompression,
  BM25Similarity,
  CharFilter as BaseCharFilter,
  CharFilterName,
  CjkBigramTokenFilter,
  CjkBigramTokenFilterScripts,
  ClassicSimilarity,
  ClassicTokenizer,
  CognitiveServicesAccount as BaseCognitiveServicesAccount,
  CognitiveServicesAccountKey,
  CommonGramTokenFilter,
  ConditionalSkill,
  CorsOptions,
  CustomEntity,
  CustomEntityAlias,
  DataChangeDetectionPolicy as BaseDataChangeDetectionPolicy,
  DataDeletionDetectionPolicy as BaseDataDeletionDetectionPolicy,
  DefaultCognitiveServicesAccount,
  DictionaryDecompounderTokenFilter,
  DistanceScoringFunction,
  DistanceScoringParameters,
  DocumentExtractionSkill,
  EdgeNGramTokenFilterSide,
  EdgeNGramTokenizer,
  ElisionTokenFilter,
  EntityLinkingSkill,
  EntityRecognitionSkillV3,
  FieldMapping,
  FieldMappingFunction,
  FreshnessScoringFunction,
  FreshnessScoringParameters,
  HighWaterMarkChangeDetectionPolicy,
  IndexerExecutionResult,
  IndexerExecutionStatus,
  IndexerStatus,
  IndexingSchedule,
  IndexProjectionMode,
  InputFieldMappingEntry,
  KeepTokenFilter,
  KeywordMarkerTokenFilter,
  KnownAzureOpenAIModelName,
  KnownBlobIndexerDataToExtract,
  KnownBlobIndexerImageAction,
  KnownBlobIndexerParsingMode,
  KnownBlobIndexerPDFTextRotationAlgorithm,
  KnownCharFilterName as KnownCharFilterNames,
  KnownCustomEntityLookupSkillLanguage,
  KnownEntityCategory,
  KnownEntityRecognitionSkillLanguage,
  KnownImageAnalysisSkillLanguage,
  KnownImageDetail,
  KnownIndexerExecutionEnvironment,
  KnownIndexProjectionMode,
  KnownKeyPhraseExtractionSkillLanguage,
  KnownLexicalTokenizerName as KnownTokenizerNames,
  KnownOcrLineEnding,
  KnownOcrSkillLanguage,
  KnownPIIDetectionSkillMaskingMode,
  KnownRegexFlags,
  KnownSearchFieldDataType,
  KnownSearchIndexerDataSourceType,
  KnownSentimentSkillLanguage,
  KnownSplitSkillLanguage,
  KnownTextSplitMode,
  KnownTextTranslationSkillLanguage,
  KnownTokenFilterName as KnownTokenFilterNames,
  KnownVectorEncodingFormat,
  KnownVectorSearchAlgorithmKind,
  KnownVectorSearchAlgorithmMetric,
  KnownVectorSearchCompressionKind,
  KnownVectorSearchCompressionTarget,
  KnownVectorSearchVectorizerKind,
  KnownVisualFeature,
  LanguageDetectionSkill,
  LengthTokenFilter,
  LexicalAnalyzer as BaseLexicalAnalyzer,
  LexicalAnalyzerName,
  LexicalTokenizer as BaseLexicalTokenizer,
  LexicalTokenizerName,
  LimitTokenFilter,
  LuceneStandardAnalyzer,
  MagnitudeScoringFunction,
  MagnitudeScoringParameters,
  MappingCharFilter,
  MergeSkill,
  MicrosoftLanguageStemmingTokenizer,
  MicrosoftLanguageTokenizer,
  MicrosoftStemmingTokenizerLanguage,
  MicrosoftTokenizerLanguage,
  NGramTokenizer,
  OcrLineEnding,
  OutputFieldMappingEntry,
  PathHierarchyTokenizerV2 as PathHierarchyTokenizer,
  PatternCaptureTokenFilter,
  PatternReplaceCharFilter,
  PatternReplaceTokenFilter,
  PhoneticEncoder,
  PhoneticTokenFilter,
  ResourceCounter,
  ScalarQuantizationCompression,
  ScalarQuantizationParameters,
  ScoringFunction as BaseScoringFunction,
  ScoringFunctionAggregation,
  ScoringFunctionInterpolation,
  SearchIndexerDataContainer,
  SearchIndexerDataIdentity as BaseSearchIndexerDataIdentity,
  SearchIndexerDataNoneIdentity,
  SearchIndexerDataUserAssignedIdentity,
  SearchIndexerError,
  SearchIndexerIndexProjectionSelector,
  SearchIndexerKnowledgeStoreBlobProjectionSelector,
  SearchIndexerKnowledgeStoreFileProjectionSelector,
  SearchIndexerKnowledgeStoreObjectProjectionSelector,
  SearchIndexerKnowledgeStoreProjection,
  SearchIndexerKnowledgeStoreProjectionSelector,
  SearchIndexerKnowledgeStoreTableProjectionSelector,
  SearchIndexerLimits,
  SearchIndexerSkill as BaseSearchIndexerSkill,
  SearchIndexerStatus,
  SearchIndexerWarning,
  SemanticConfiguration,
  SemanticField,
  SemanticPrioritizedFields,
  SemanticSearch,
  SentimentSkillV3,
  ServiceCounters,
  ServiceLimits,
  ShaperSkill,
  ShingleTokenFilter,
  Similarity,
  SnowballTokenFilter,
  SnowballTokenFilterLanguage,
  SoftDeleteColumnDeletionDetectionPolicy,
  SqlIntegratedChangeTrackingPolicy,
  StemmerOverrideTokenFilter,
  StemmerTokenFilter,
  StemmerTokenFilterLanguage,
  StopAnalyzer,
  StopwordsList,
  StopwordsTokenFilter,
  Suggester as SearchSuggester,
  SynonymTokenFilter,
  TagScoringFunction,
  TagScoringParameters,
  TextWeights,
  TokenCharacterKind,
  TokenFilter as BaseTokenFilter,
  TokenFilterName,
  TruncateTokenFilter,
  UaxUrlEmailTokenizer,
  UniqueTokenFilter,
  VectorEncodingFormat,
  VectorSearchCompression as BaseVectorSearchCompression,
  VectorSearchCompressionKind,
  VectorSearchCompressionTarget,
  VectorSearchProfile,
  VectorSearchVectorizerKind,
  WordDelimiterTokenFilter,
} from "./generated/service/models";
export { default as GeographyPoint } from "./geographyPoint";
export { IndexDocumentsBatch } from "./indexDocumentsBatch";
export {
  AutocompleteOptions,
  AutocompleteRequest,
  BaseSearchRequestOptions,
  BaseVectorQuery,
  CountDocumentsOptions,
  DeleteDocumentsOptions,
  ExcludedODataTypes,
  ExtractDocumentKey,
  ExtractiveQueryAnswer,
  ExtractiveQueryCaption,
  GetDocumentOptions,
  IndexDocumentsAction,
  IndexDocumentsOptions,
  ListSearchResultsPageSettings,
  MergeDocumentsOptions,
  MergeOrUploadDocumentsOptions,
  NarrowedModel,
  QueryAnswer,
  QueryCaption,
  SearchDocumentsPageResult,
  SearchDocumentsResult,
  SearchDocumentsResultBase,
  SearchFieldArray,
  SearchIndexingBufferedSenderDeleteDocumentsOptions,
  SearchIndexingBufferedSenderFlushDocumentsOptions,
  SearchIndexingBufferedSenderMergeDocumentsOptions,
  SearchIndexingBufferedSenderMergeOrUploadDocumentsOptions,
  SearchIndexingBufferedSenderOptions,
  SearchIndexingBufferedSenderUploadDocumentsOptions,
  SearchIterator,
  SearchOptions,
  SearchPick,
  SearchRequestOptions,
  SearchRequestQueryTypeOptions,
  SearchResult,
  SelectArray,
  SelectFields,
  SemanticErrorMode,
  SemanticErrorReason,
  SemanticSearchOptions,
  SemanticSearchResultsType,
  SuggestDocumentsResult,
  SuggestNarrowedModel,
  SuggestOptions,
  SuggestRequest,
  SuggestResult,
  UnionToIntersection,
  UploadDocumentsOptions,
  VectorFilterMode,
  VectorizableTextQuery,
  VectorizedQuery,
  VectorQuery,
  VectorQueryKind,
  VectorSearchOptions,
} from "./indexModels";
export { odata } from "./odata";
export { KnownSearchAudience } from "./searchAudience";
export { SearchClient, SearchClientOptions } from "./searchClient";
export { SearchIndexClient, SearchIndexClientOptions } from "./searchIndexClient";
export { SearchIndexerClient, SearchIndexerClientOptions } from "./searchIndexerClient";
export {
  DEFAULT_BATCH_SIZE,
  DEFAULT_FLUSH_WINDOW,
  DEFAULT_RETRY_COUNT,
  IndexDocumentsClient,
  SearchIndexingBufferedSender,
} from "./searchIndexingBufferedSender";
export {
  AnalyzeRequest,
  AnalyzeTextOptions,
  AzureOpenAIEmbeddingSkill,
  AzureOpenAIParameters,
  AzureOpenAIVectorizer,
  BaseVectorSearchAlgorithmConfiguration,
  BaseVectorSearchVectorizer,
  BlobIndexerDataToExtract,
  BlobIndexerImageAction,
  BlobIndexerParsingMode,
  BlobIndexerPDFTextRotationAlgorithm,
  CharFilter,
  CognitiveServicesAccount,
  ComplexDataType,
  ComplexField,
  CreateDataSourceConnectionOptions,
  CreateIndexerOptions,
  CreateIndexOptions,
  CreateorUpdateDataSourceConnectionOptions,
  CreateorUpdateIndexerOptions,
  CreateOrUpdateIndexOptions,
  CreateOrUpdateSkillsetOptions,
  CreateOrUpdateSynonymMapOptions,
  CreateSkillsetOptions,
  CreateSynonymMapOptions,
  CustomAnalyzer,
  CustomEntityLookupSkill,
  CustomEntityLookupSkillLanguage,
  DataChangeDetectionPolicy,
  DataDeletionDetectionPolicy,
  DeleteDataSourceConnectionOptions,
  DeleteIndexerOptions,
  DeleteIndexOptions,
  DeleteSkillsetOptions,
  DeleteSynonymMapOptions,
  EdgeNGramTokenFilter,
  EntityCategory,
  EntityRecognitionSkill,
  EntityRecognitionSkillLanguage,
  ExhaustiveKnnAlgorithmConfiguration,
  ExhaustiveKnnParameters,
  GetDataSourceConnectionOptions,
  GetIndexerOptions,
  GetIndexerStatusOptions,
  GetIndexOptions,
  GetIndexStatisticsOptions,
  GetServiceStatisticsOptions,
  GetSkillSetOptions,
  GetSynonymMapsOptions,
  HnswAlgorithmConfiguration,
  HnswParameters,
  ImageAnalysisSkill,
  ImageAnalysisSkillLanguage,
  ImageDetail,
  IndexerExecutionEnvironment,
  IndexingParameters,
  IndexingParametersConfiguration,
  IndexIterator,
  IndexNameIterator,
  KeyPhraseExtractionSkill,
  KeyPhraseExtractionSkillLanguage,
  KeywordTokenizer,
  KnownAnalyzerNames,
  LexicalAnalyzer,
  LexicalTokenizer,
  ListDataSourceConnectionsOptions,
  ListIndexersOptions,
  ListIndexesOptions,
  ListSkillsetsOptions,
  ListSynonymMapsOptions,
  LuceneStandardTokenizer,
  NGramTokenFilter,
  OcrSkill,
  OcrSkillLanguage,
  PatternAnalyzer,
  PatternTokenizer,
  PIIDetectionSkill,
  PIIDetectionSkillMaskingMode,
  RegexFlags,
  ResetIndexerOptions,
  RunIndexerOptions,
  ScoringFunction,
  ScoringProfile,
  SearchField,
  SearchFieldDataType,
  SearchIndex,
  SearchIndexer,
  SearchIndexerDataIdentity,
  SearchIndexerDataSourceConnection,
  SearchIndexerDataSourceType,
  SearchIndexerIndexProjection,
  SearchIndexerIndexProjectionParameters,
  SearchIndexerKnowledgeStore,
  SearchIndexerKnowledgeStoreParameters,
  SearchIndexerSkill,
  SearchIndexerSkillset,
  SearchIndexStatistics,
  SearchResourceEncryptionKey,
  SearchServiceStatistics,
  SentimentSkill,
  SentimentSkillLanguage,
  SimilarityAlgorithm,
  SimpleField,
  SplitSkill,
  SplitSkillLanguage,
  SynonymMap,
  TextSplitMode,
  TextTranslationSkill,
  TextTranslationSkillLanguage,
  TokenFilter,
  VectorSearch,
  VectorSearchAlgorithmConfiguration,
  VectorSearchAlgorithmKind,
  VectorSearchAlgorithmMetric,
  VectorSearchCompression,
  VectorSearchVectorizer,
  VisualFeature,
  WebApiParameters,
  WebApiSkill,
  WebApiVectorizer,
} from "./serviceModels";
export { createSynonymMapFromFile } from "./synonymMapHelper";
